import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Calendar as CalendarIcon, MapPin, Trophy, Clock, Info, Search, Filter } from 'lucide-react';
import { useStore } from '../data/store';
import { formatTime } from '../lib/utils';

const Schedule = () => {
  const { games } = useStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const formatDate = (dateString: string) => {
    try {
      let date;
      if (dateString.includes('T')) {
        date = new Date(dateString);
      } else if (dateString.includes('/')) {
        date = new Date(dateString);
      } else {
        date = new Date(dateString + 'T12:00:00');
      }

      if (isNaN(date.getTime())) {
        console.error('Invalid date:', dateString);
        return { month: 'TBD', day: '?', weekday: 'TBD', timestamp: 0 };
      }

      return {
        month: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
        day: date.getDate(),
        weekday: date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase(),
        timestamp: date.getTime()
      };
    } catch (error) {
      console.error('Error formatting date:', error);
      return { month: 'TBD', day: '?', weekday: 'TBD', timestamp: 0 };
    }
  };

  const getGameOutcome = (cardinals: number, opponent: number) => {
    if (cardinals > opponent) return 'win';
    if (cardinals < opponent) return 'loss';
    return 'tie';
  };

  const now = new Date().getTime();

  const filteredGames = games.filter(game => {
    const matchesSearch = game.opponent.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         game.venue?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesLocation = locationFilter === 'all' ||
                           (locationFilter === 'home' && game.location === 'Home') ||
                           (locationFilter === 'away' && game.location === 'Away');

    const gameDate = formatDate(game.date).timestamp;
    const isPast = gameDate < now;

    const matchesStatus = statusFilter === 'all' ||
                         (statusFilter === 'upcoming' && !isPast) ||
                         (statusFilter === 'completed' && isPast);

    return matchesSearch && matchesLocation && matchesStatus;
  });

  const sortedGames = [...filteredGames].sort((a, b) => {
    const dateA = formatDate(a.date).timestamp;
    const dateB = formatDate(b.date).timestamp;
    return dateA - dateB;
  });

  return (
    <div className="min-h-screen flex flex-col bg-[#111111]">
      <Navbar />
      
      <main className="flex-1 mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-8">
            <CalendarIcon className="w-8 h-8 text-[#8B0000]" />
            <h1 className="text-4xl font-bold text-white">Game Schedule</h1>
          </div>

          <div className="mb-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="relative sm:col-span-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search opponent or field..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-[#222222] text-white rounded-lg border border-gray-700 focus:border-[#8B0000] focus:ring focus:ring-[#8B0000]/50 transition-colors"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-[#222222] text-white rounded-lg border border-gray-700 focus:border-[#8B0000] focus:ring focus:ring-[#8B0000]/50 transition-colors appearance-none cursor-pointer"
              >
                <option value="all">All Locations</option>
                <option value="home">Home Games</option>
                <option value="away">Away Games</option>
              </select>
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-[#222222] text-white rounded-lg border border-gray-700 focus:border-[#8B0000] focus:ring focus:ring-[#8B0000]/50 transition-colors appearance-none cursor-pointer"
              >
                <option value="all">All Games</option>
                <option value="upcoming">Upcoming</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>

          {sortedGames.length > 0 ? (
            <>
              <div className="space-y-6">
                {sortedGames.map((game) => {
                  const { month, day, weekday } = formatDate(game.date);
                  const isHomeGame = game.location === 'Home';
                  return (
                    <div 
                      key={game.id}
                      className="bg-[#222222] rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow border border-white/10"
                    >
                      <div className="flex flex-col md:flex-row">
                        {/* Date Column */}
                        <div className="bg-[#1A1A1A] p-6 flex flex-col items-center justify-center md:w-48">
                          <p className="text-gray-400 uppercase tracking-wider text-sm">{weekday}</p>
                          <p className="text-4xl font-bold text-white mt-1">{day}</p>
                          <p className="text-xl text-cardinal uppercase">{month}</p>
                          {game.isDoubleHeader && (
                            <span className="mt-2 px-3 py-1 bg-cardinal text-white text-xs font-semibold rounded-full">
                              Double Header
                            </span>
                          )}
                        </div>

                        {/* Game Details */}
                        <div className="flex-1 p-6">
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                            <div>
                              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                                {isHomeGame ? (
                                  <>
                                    <span className="text-cardinal">vs</span> {game.opponent}
                                  </>
                                ) : (
                                  <>
                                    <span className="text-gray-400">at</span> {game.opponent}
                                  </>
                                )}
                              </h2>
                              
                              <div className="mt-2 space-y-2">
                                <div className="flex items-center gap-2 text-gray-400">
                                  <Clock className="w-4 h-4" />
                                  <span>{formatTime(game.time)}{game.isDoubleHeader && game.time2 ? ` & ${formatTime(game.time2)}` : ''}</span>
                                </div>
                                
                                <div className="flex items-center gap-2 text-gray-400">
                                  <MapPin className="w-4 h-4" />
                                  <span>{game.venue}</span>
                                  {game.address && (
                                    <span className="text-sm">({game.address})</span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Game Result */}
                            {game.score && (
                              <div className="bg-[#1A1A1A] rounded-lg p-6">
                                {game.isDoubleHeader ? (
                                  <div className="space-y-6">
                                    {/* Game 1 */}
                                    {game.score.game1 && (
                                      <div>
                                        <div className="flex items-center justify-between gap-4 mb-2">
                                          <h3 className="text-sm font-medium text-gray-400">Game 1</h3>
                                          <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                                            getGameOutcome(game.score.game1.cardinals, game.score.game1.opponent) === 'win'
                                              ? 'bg-green-500/20 text-green-400'
                                              : getGameOutcome(game.score.game1.cardinals, game.score.game1.opponent) === 'loss'
                                              ? 'bg-red-500/20 text-red-400'
                                              : 'bg-gray-500/20 text-gray-400'
                                          }`}>
                                            {getGameOutcome(game.score.game1.cardinals, game.score.game1.opponent).toUpperCase()}
                                          </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                          <div className="text-center flex-1">
                                            <p className="text-sm text-gray-400 mb-1">Cardinals</p>
                                            <p className="text-3xl font-bold text-white">
                                              {game.score.game1.cardinals}
                                            </p>
                                          </div>
                                          <div className="px-4">
                                            <span className="text-2xl text-gray-600">-</span>
                                          </div>
                                          <div className="text-center flex-1">
                                            <p className="text-sm text-gray-400 mb-1">{game.opponent}</p>
                                            <p className="text-3xl font-bold text-white">
                                              {game.score.game1.opponent}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    {/* Game 2 */}
                                    {game.score.game2 && (
                                      <div>
                                        <div className="flex items-center justify-between gap-4 mb-2">
                                          <h3 className="text-sm font-medium text-gray-400">Game 2</h3>
                                          <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                                            getGameOutcome(game.score.game2.cardinals, game.score.game2.opponent) === 'win'
                                              ? 'bg-green-500/20 text-green-400'
                                              : getGameOutcome(game.score.game2.cardinals, game.score.game2.opponent) === 'loss'
                                              ? 'bg-red-500/20 text-red-400'
                                              : 'bg-gray-500/20 text-gray-400'
                                          }`}>
                                            {getGameOutcome(game.score.game2.cardinals, game.score.game2.opponent).toUpperCase()}
                                          </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                          <div className="text-center flex-1">
                                            <p className="text-sm text-gray-400 mb-1">Cardinals</p>
                                            <p className="text-3xl font-bold text-white">
                                              {game.score.game2.cardinals}
                                            </p>
                                          </div>
                                          <div className="px-4">
                                            <span className="text-2xl text-gray-600">-</span>
                                          </div>
                                          <div className="text-center flex-1">
                                            <p className="text-sm text-gray-400 mb-1">{game.opponent}</p>
                                            <p className="text-3xl font-bold text-white">
                                              {game.score.game2.opponent}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <>
                                    <div className="flex items-center justify-between gap-4 mb-2">
                                      <h3 className="text-sm font-medium text-gray-400">Final Score</h3>
                                      <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                                        getGameOutcome(game.score.cardinals!, game.score.opponent!) === 'win'
                                          ? 'bg-green-500/20 text-green-400'
                                          : getGameOutcome(game.score.cardinals!, game.score.opponent!) === 'loss'
                                          ? 'bg-red-500/20 text-red-400'
                                          : 'bg-gray-500/20 text-gray-400'
                                      }`}>
                                        {getGameOutcome(game.score.cardinals!, game.score.opponent!).toUpperCase()}
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <div className="text-center flex-1">
                                        <p className="text-sm text-gray-400 mb-1">Cardinals</p>
                                        <p className="text-3xl font-bold text-white">
                                          {game.score.cardinals}
                                        </p>
                                      </div>
                                      <div className="px-4">
                                        <span className="text-2xl text-gray-600">-</span>
                                      </div>
                                      <div className="text-center flex-1">
                                        <p className="text-sm text-gray-400 mb-1">{game.opponent}</p>
                                        <p className="text-3xl font-bold text-white">
                                          {game.score.opponent}
                                        </p>
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Game Length Information */}
              <div className="mt-8 bg-[#222222] rounded-lg p-6 border border-white/10">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-cardinal mt-1" />
                  <div className="space-y-2">
                    <p className="text-gray-400">
                      <span className="text-white">*</span> Double headers consist of two 7-inning games
                    </p>
                    <p className="text-gray-400">
                      <span className="text-white">*</span> Non-double header games are 9 innings
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-[#222222] p-8 rounded-lg text-center">
              <CalendarIcon className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No games have been scheduled yet.</p>
              <p className="text-gray-500 mt-2">Check back soon for upcoming games.</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Schedule;